import React, { useEffect, useState } from 'react';
import LoadingOverlay from 'react-loading-overlay';
import './ChecklistForm.scss';
import ChecklistQuestion from './ChecklistQuestion';
import ChecklistConfirm from './ChecklistConfirm';
import {
  fetchBeginChallengeQuestions,
  submitBeginChallengeAnswers,
} from '../beginChallenge.service';

/**
 * Reusable checklist/assessment form for begin challenge.
 * Fetches questions from API, renders all at once (scrollable),
 * and submits answers when all questions are answered.
 *
 * @param {string}   assessmentId - assessment ID passed from parent
 * @param {function} onFinish     - callback called with API result { success, answerId } after submit
 *
 * Usage:
 *   <ChecklistForm
 *     assessmentId="abc123"
 *     onFinish={(result) => history.push(`/next-page?answer_id=${result.answerId}`)}
 *   />
 */
const ChecklistForm = ({ assessmentId, onFinish }) => {
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [fetchLoading, setFetchLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);
  const [showResultModal, setShowResultModal] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!assessmentId) return;
    setFetchLoading(true);
    fetchBeginChallengeQuestions(assessmentId)
      .then((data) => {
        const sorted = (data?.hw_assessments_questions || []).sort(
          (a, b) => (a.display_order || 0) - (b.display_order || 0),
        );
        setQuestions(sorted);
      })
      .catch((err) => {
        console.error('ChecklistForm: fetch questions error', err);
        setError('ไม่สามารถโหลดคำถามได้ กรุณาลองใหม่อีกครั้ง');
      })
      .finally(() => setFetchLoading(false));
  }, [assessmentId]);

  const handleChoiceSelect = (question, choice) => {
    setAnswers((prev) => ({
      ...prev,
      [question._id]: {
        questionId: question._id,
        answerId: choice._id,
        score: choice.score || 0,
      },
    }));
  };

  const allAnswered =
    questions.length > 0 && Object.keys(answers).length === questions.length;

  const handleSubmit = async () => {
    if (!allAnswered || submitLoading) return;
    try {
      setSubmitLoading(true);
      const answersArray = Object.values(answers);
      const result = await submitBeginChallengeAnswers({
        assessmentId,
        answers: answersArray,
      });
      // Show loading modal for 5 seconds before calling onFinish
      setSubmitLoading(false);
      setShowResultModal(true);
      setTimeout(() => {
        setShowResultModal(false);
        if (onFinish) {
          onFinish(result);
        }
      }, 5000);
    } catch (err) {
      console.error('ChecklistForm: submit error', err);
      setSubmitLoading(false);
      setError('ไม่สามารถส่งคำตอบได้ กรุณาลองใหม่อีกครั้ง');
    }
  };

  if (fetchLoading) {
    return (
      <LoadingOverlay
        active
        spinner
        text="Loading"
        styles={{
          wrapper: {
            width: '100%',
            minHeight: '200px',
            position: 'relative',
          },
        }}
      >
        <div />
      </LoadingOverlay>
    );
  }

  if (error) {
    return (
      <div className="checklist-form__error">
        <p>{error}</p>
      </div>
    );
  }

  if (!questions.length) {
    return (
      <div className="checklist-form__empty">
        <p>ไม่พบคำถาม</p>
      </div>
    );
  }

  return (
    <div className="checklist-form">
      <div className="checklist-form__section-header">
        <div className="checklist-form__section-name">คำถาม</div>
        <div className="checklist-form__count-container">
          <img
            className="checklist-form__count-image"
            src="/images/nutrition-and-rehabilitation/number-of-item.svg"
            alt="จำนวนข้อ"
          />
          <div className="checklist-form__count-text">{questions.length}</div>
          <div className="checklist-form__count-unit">ข้อ</div>
        </div>
      </div>
      <div className="checklist-form__questions">
        {questions.map((question, index) => (
          <div key={question._id} className="checklist-form__question-wrapper">
            <ChecklistQuestion
              questionNumber={index + 1}
              questionText={question.question}
              choices={question.question_value || []}
              selectedChoiceId={answers[question._id]?.answerId || null}
              onChoiceSelect={(choice) => handleChoiceSelect(question, choice)}
            />
          </div>
        ))}
      </div>

      <div className="checklist-form__footer">
        <ChecklistConfirm
          width="100%"
          disabled={!allAnswered}
          loading={submitLoading}
          onClick={handleSubmit}
        />
      </div>

      {showResultModal && (
        <div className="checklist-form__result-modal">
          <div className="checklist-form__result-modal-box">
            <div className="checklist-form__result-modal-spinner" />
            <div className="checklist-form__result-modal-text">กำลังประมวลผล...</div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChecklistForm;
